import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CLIENT_TIMEZONES, CLIENT_TRANSITIONS, type ClientReadinessView, type ClientState, type ClientView, type CompanyUserView } from '@lp/contracts';
import { Check, KeyRound, Minus, X } from 'lucide-react';
import { useState } from 'react';
import { errorText, fieldProblems, get, patch, post, put } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, Select } from './ui';

const ACTION: Record<ClientState, string> = { live: 'Make live', suspended: 'Suspend', closed: 'Close', testing: 'Back to testing' };

function useClientRefresh(clientId: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['client', clientId] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
    void qc.invalidateQueries({ queryKey: ['audit', 'client', clientId] });
  };
}

/** the state buttons the client's state allows (Testing → Live only when ready; Suspend / Resume; Close — final) */
export function ClientStateActions({ client }: { client: ClientView }) {
  const t = useT();
  const superAdmin = useIsSuperAdmin();
  const [to, setTo] = useState<ClientState | null>(null);
  if (!superAdmin) return null;
  return (
    <>
      {CLIENT_TRANSITIONS[client.state].map((s) => (
        <Button key={s} variant={s === 'live' ? 'primary' : 'secondary'} onClick={() => setTo(s)} className={s === 'closed' ? 'text-red-700' : undefined}>
          {t(s === 'live' && client.state === 'suspended' ? 'Resume' : ACTION[s])}
        </Button>
      ))}
      {to && <StateDialog client={client} to={to} onClose={() => setTo(null)} />}
    </>
  );
}

function StateDialog({ client, to, onClose }: { client: ClientView; to: ClientState; onClose: () => void }) {
  const t = useT();
  const refresh = useClientRefresh(client.id);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const ready = useQuery({ queryKey: ['client', client.id, 'readiness'], queryFn: () => get<ClientReadinessView>(`/platform/clients/${client.id}/readiness`), enabled: to === 'live' && client.state === 'testing' });
  const change = useMutation({
    mutationFn: () => put<ClientView>(`/platform/clients/${client.id}/state`, { state: to, ...(reason.trim() ? { reason } : {}), ...(to === 'closed' ? { confirmCode: confirm } : {}) }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const needsReason = to === 'suspended' || to === 'closed';
  const blocked = (to === 'live' && client.state === 'testing' && !ready.data?.ready) || (needsReason && !reason.trim()) || (to === 'closed' && confirm.trim().toUpperCase() !== client.code);
  const label = t(to === 'live' && client.state === 'suspended' ? 'Resume' : ACTION[to]);
  return (
    <Dialog
      title={`${label} — ${client.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => change.mutate()} disabled={blocked || change.isPending} className={to === 'closed' ? 'bg-red-600 hover:bg-red-700' : undefined}>
            {label}
          </Button>
        </>
      }
    >
      {to === 'live' && client.state === 'testing' && (
        <>
          <p>{t('A company goes live only when everything below that is required is done.')}</p>
          {ready.isLoading ? (
            <Loading />
          ) : ready.data ? (
            <ul className="space-y-1" data-testid="readiness">
              {ready.data.items.map((i) => (
                <li key={i.key} className="flex items-start gap-2">
                  {i.ok ? <Check size={14} className="mt-0.5 text-emerald-600" /> : i.blocking ? <X size={14} className="mt-0.5 text-red-600" /> : <Minus size={14} className="mt-0.5 text-amber-600" />}
                  <span>
                    {t(i.label)}
                    {!i.blocking && <span className="ml-1 text-xs text-ink-500">({t('advice')})</span>}
                    {i.detail && <span className="block text-xs text-ink-500">{i.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
      {to === 'suspended' && <p>{t('A suspended company cannot sell. Its tickets, results and history stay.')}</p>}
      {to === 'closed' && <p className="text-red-700">{t('Closing is final: the company cannot sell again. Its history stays.')}</p>}
      {needsReason && (
        <Field label={t('Reason')}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} autoFocus />
        </Field>
      )}
      {to === 'closed' && (
        <Field label={`${t('Type the client code to confirm')}: ${client.code}`}>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
      )}
      {change.error && <ErrorBox text={[errorText(change.error), ...fieldProblems(change.error)].join(' — ')} />}
    </Dialog>
  );
}

/** names, time zone, contact and notes — the code and the address name never change */
export function EditClientDialog({ client, onClose }: { client: ClientView; onClose: () => void }) {
  const t = useT();
  const refresh = useClientRefresh(client.id);
  const [f, setF] = useState({ code: client.code, slug: client.slug, nameEn: client.nameEn, nameKm: client.nameKm, timezone: client.timezone, contactName: client.contactName ?? '', contactPhone: client.contactPhone ?? '', contactEmail: client.contactEmail ?? '', notes: client.notes ?? '' });
  const save = useMutation({
    mutationFn: () => {
      const orNull = (v: string) => (v.trim() ? v.trim() : null);
      return patch<ClientView>(`/platform/clients/${client.id}`, { ...(f.code !== client.code ? { code: f.code } : {}), ...(f.slug !== client.slug ? { slug: f.slug } : {}), nameEn: f.nameEn, nameKm: f.nameKm, timezone: f.timezone, contactName: orNull(f.contactName), contactPhone: orNull(f.contactPhone), contactEmail: orNull(f.contactEmail), notes: orNull(f.notes) });
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog
      title={`${t('Edit details')} — ${client.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !f.nameEn.trim() || !f.nameKm.trim()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label={t('Code')}>
          <Input value={f.code} onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toUpperCase() }))} maxLength={10} />
        </Field>
        <Field label={t('Address name')}>
          <Input value={f.slug} onChange={(e) => setF((x) => ({ ...x, slug: e.target.value.toLowerCase() }))} maxLength={40} />
        </Field>
      </div>
      {f.slug !== client.slug && <p className="text-xs text-amber-700">{t('The automatic address moves to the new name.')}</p>}
      <Field label={t('Name (English)')}>
        <Input value={f.nameEn} onChange={set('nameEn')} maxLength={80} />
      </Field>
      <Field label={t('Name (Khmer)')}>
        <Input value={f.nameKm} onChange={set('nameKm')} maxLength={80} />
      </Field>
      <Field label={t('Time zone')}>
        <Select value={f.timezone} onChange={set('timezone')} className="w-full">
          {CLIENT_TIMEZONES.map((z) => (
            <option key={z}>{z}</option>
          ))}
        </Select>
      </Field>
      <Field label={t('Contact name')}>
        <Input value={f.contactName} onChange={set('contactName')} maxLength={80} />
      </Field>
      <Field label={t('Contact phone')}>
        <Input value={f.contactPhone} onChange={set('contactPhone')} maxLength={30} />
      </Field>
      <Field label={t('Contact email')}>
        <Input value={f.contactEmail} onChange={set('contactEmail')} maxLength={120} />
      </Field>
      <Field label={t('Notes')}>
        <Input value={f.notes} onChange={set('notes')} maxLength={1000} />
      </Field>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

/** the company's own users — its admin now; staff and permissions arrive in Phase 9 */
export function CompanyUsers({ clientId }: { clientId: number }) {
  const t = useT();
  const superAdmin = useIsSuperAdmin();
  const users = useQuery({ queryKey: ['client', clientId, 'users'], queryFn: () => get<CompanyUserView[]>(`/platform/clients/${clientId}/users`) });
  const reset = useMutation({ mutationFn: (id: number) => post<{ username: string; initialPassword: string }>(`/platform/clients/${clientId}/users/${id}/password`) });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CompanyUserView | null>(null);
  return (
    <Card title={t('Company users')} className="mt-4">
      {users.isLoading ? (
        <Loading />
      ) : users.error ? (
        <ErrorBox text={errorText(users.error)} />
      ) : !users.data?.length ? (
        <p className="text-sm text-ink-600">{t('No user yet.')}</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {users.data.map((u) => (
              <tr key={u.id} className="border-b border-ink-100 last:border-0" data-testid={`company-user-${u.username}`}>
                <td className="py-2 pr-3">
                  <span className="font-medium">{u.displayName}</span> <span className="font-mono text-xs text-ink-500">{u.username}</span>
                </td>
                <td className="py-2 pr-3">
                  <Badge tone={u.role === 'admin' ? 'blue' : 'gray'}>{u.role === 'admin' ? t('Admin') : (u.position ?? t('Staff'))}</Badge> {u.mustChangePassword && <Badge tone="amber">{t('Must change password')}</Badge>}
                </td>
                <td className="py-2 pr-3 text-xs text-ink-500">
                  {t('Last sign-in')}: {dateTime(u.lastLoginAt)}
                </td>
                <td className="py-2 text-right">
                  {superAdmin && (
                    <span className="inline-flex gap-1">
                      <Button variant="ghost" onClick={() => setEditing(u)}>
                        {t('Edit')}
                      </Button>
                      <Button variant="ghost" icon={<KeyRound size={14} />} onClick={() => reset.mutate(u.id)} disabled={reset.isPending}>
                        {t('New first password')}
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {reset.data && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="mb-1 text-xs text-amber-900">{t('Shown only now — give it to the user privately. The old password no longer works.')}</p>
          <span className="font-mono">{reset.data.username}</span> · <span className="font-mono text-base font-semibold tracking-wider">{reset.data.initialPassword}</span>
        </div>
      )}
      {reset.error && <ErrorBox text={errorText(reset.error)} />}
      <p className="mt-3 text-xs text-ink-500">{t('The company signs in with its code on the sign-in page (Company) and manages its own staff there.')}</p>
      {editing && <EditCompanyUser clientId={clientId} user={editing} onDone={() => { setEditing(null); void qc.invalidateQueries({ queryKey: ['client', clientId, 'users'] }); }} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/** the Super Admin changes a company user: name, username, position, on / off */
function EditCompanyUser({ clientId, user, onDone, onClose }: { clientId: number; user: CompanyUserView; onDone: () => void; onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({ displayName: user.displayName, username: user.username, position: user.position ?? '', active: user.status === 'active' });
  const save = useMutation({
    mutationFn: () =>
      patch(`/platform/clients/${clientId}/users/${user.id}`, {
        ...(f.displayName !== user.displayName ? { displayName: f.displayName } : {}),
        ...(f.username !== user.username ? { username: f.username } : {}),
        ...(f.position !== (user.position ?? '') ? { position: f.position.trim() || null } : {}),
        ...(f.active !== (user.status === 'active') ? { status: f.active ? 'active' : 'disabled' } : {}),
      }),
    onSuccess: onDone,
  });
  const changed = f.displayName !== user.displayName || f.username !== user.username || f.position !== (user.position ?? '') || f.active !== (user.status === 'active');
  return (
    <Dialog
      title={`${t('Edit')} — ${user.username}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!changed || !f.displayName.trim() || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Display name')}>
        <Input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Username')}>
        <Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase() })} maxLength={40} />
      </Field>
      {user.role !== 'admin' && (
        <Field label={t('Position')}>
          <Input value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} maxLength={60} />
        </Field>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
        {t('Can sign in')}
      </label>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}
